import React, {memo, useEffect, useState, useCallback} from "react"
import {onClickHandler} from '../../../../../utils/onclick';
import IconAttention from '../../../../../assets/images/icons/attention.png'
import './index.scss';
import {RawDevice} from '../../../../../store/setup/state';
import {useSelector, useDispatch} from "react-redux"
import {useOrientation} from '../../../../../hooks/orientation';
import {useRPC} from '../../../../../hooks/rpc';
import {useMessage} from '../../../../../hooks/message';
import {useLocalization} from "../../../../../hooks/localization";
import {setRSSIAction, setVersionAction, setHardwareVersionCompatibility} from "../../../../../store/control/actions"
import { useLocalStorage } from "../../../../../hooks/use-storage";
import { MappingLogPopupScreen } from "../SetupMatchScreen";
import { Loader } from '../../../../../components/Loader';

declare function renderIf(predicate: any): (item: any, elseValue?: any) => React.ReactNode;

type Props = {
    onClosePressed: () => void;
    rawDevice: RawDevice;
    deviceName: string;
    deviceImage: string;
};

function createNullArray(count) {
    if (!count) {
        return [];
    }

    return Array(count).fill(null);
}


const MatchedItemButton = memo(function MatchedItemButton({color, text, onClick, disabled}: { color: string; text: string; onClick: () => void; disabled?: boolean }) {
    const {t} = useLocalization();
    return (
        <div className={'matched-item-button ' + color} {...onClickHandler(disabled ? null : onClick)} >
            <span dangerouslySetInnerHTML={{__html: disabled ? t('searching') : text}}/>
        </div>
    );
});


export const SetupIOTestComponent = memo(function SetupIOTestComponent(props: Props) {
    const {
        onClosePressed,
        rawDevice,
        deviceName,
        deviceImage,
    } = props;
    const {t} = useLocalization();
    const [rpc] = useRPC();
    const {failure, success} = useMessage()
    const orientation = useOrientation();
    const deviceType = useSelector(state => state.control.bridgeDeviceTypeList.find(d => d.id === rawDevice.device_type_id));
    const dispatch = useDispatch()
    const [outputs, setOutputs] = useState<boolean[]>(createNullArray(deviceType?.output_count));
    const [inputs, setInputs] = useState<boolean[]>(createNullArray(deviceType?.input_count));
    const versionStatusControl = useSelector(state => state.control.versionStatus)
    const rssiStatus = useSelector(state => state.control.rssiStatus)
    const hardwareVersionCompatibility = useSelector(state => state.control.hardwareVersionCompatibility)
    const [openHardwareVersionPopup, setOpenHardwareVersionPopup] = useState(false)
    const [hardwareVersionResults, setHardwareVersionResults] = useState<string[]>([]);
    const [versionCheckInProgress, setVersionCheckInProgress] = useState(false);

    
    const [outputLoading, setOutputLoading] = useState<(boolean)[]>(createNullArray(deviceType?.output_count));
    const [inputLoading, setInputLoading] = useState<(boolean)[]>(createNullArray(deviceType?.input_count));

    useEffect(() => {
        if (!versionStatusControl || !deviceType) return;
    
        const fetch = async () => {
            try {
                await rpc.CheckHardwareVersionCompatibility(
                    rawDevice.gateway_id,
                    deviceType.id,
                    versionStatusControl
                );
            } catch (error) {
                setHardwareVersionResults([
                    `${t('version')}: ${versionStatusControl}`,  
                ]);
            } finally {
                setVersionCheckInProgress(false);
                dispatch(setVersionAction(null));
            }
        };
    
        fetch();
    }, [versionStatusControl, deviceType, dispatch, rawDevice.gateway_id, rpc, t]);
    

   
    useEffect(() => {
        if (!hardwareVersionCompatibility) return;
    
        const {hardware_device_version = {}} = hardwareVersionCompatibility;
        const allowedKeys = ['version', 'error_level', 'description'];
        const lines = [
            ...Object.entries(hardware_device_version)
                .filter(([key]) => allowedKeys.includes(key))
                .sort(([a], [b]) => allowedKeys.indexOf(a) - allowedKeys.indexOf(b))
                .flatMap(([key, value]) => {
                    let displayValue = value;
                    if (typeof value === 'object') {
                        displayValue = JSON.stringify(value);
                    }
                    if (key === 'error_level') {
                        const dotColor = {
                            red: 'Critical',
                            green: 'Info',
                            yellow: 'Warning'
                        }[value as string] || 'Unknown';
                        return [`${t('error_level')}: ${dotColor}`];
                    }
                    if (key === 'version') {
                        return [`${t('version')}: ${displayValue}`];
                    }
                    if (key === 'description') {
                        return [`${t('description')}:`, `${displayValue}`];
                    }
                    return [`${key}: ${displayValue}`];
                }),
        ];
        
        setHardwareVersionResults(lines);
        setVersionCheckInProgress(false);
        dispatch(setHardwareVersionCompatibility(null));
    }, [hardwareVersionCompatibility, t, dispatch, rawDevice]);
    
    useEffect(() => {
        if (rssiStatus != null) {
            success(rssiStatus, true)
            dispatch(setRSSIAction(null))
        }
    }, [success, rssiStatus, t, dispatch])

    const [backLastStatus, setBackLastStatus] = useLocalStorage<{slot: number}[]>(`io-test-slots(${rawDevice?.unique_device_id})`, [])
    const outputClick = (slot: number) => {

        setOutputLoading(l => l.map((v, i) => (i === slot ? true : v)));
        setTimeout(() => {
            setOutputLoading(l => l.map((v, i) => (i === slot ? false : v)));
        }, 500);

        const newOutputs = outputs.map((o, i) => {
            if (i === slot) {
                return !o;
            }

            return o == null ? null : false;
        });
        rpc.setDirectOutput(rawDevice.gateway_id, rawDevice.unique_device_id, slot, newOutputs[slot] ? 1 : 0)
            .then(() => setOutputs(newOutputs))
            .catch(failure);
    };


    const outputClickForIODevices = (slot: number) => {
        setOutputLoading(l => l.map((v, i) => (i === slot ? true : v)));
        setTimeout(() => {
            setOutputLoading(l => l.map((v, i) => (i === slot ? false : v)));
        }, 500);

        if (deviceType?.is_io_device) {
            const sameSlot = backLastStatus.every(r => r.slot !== slot)
            if (sameSlot) {
                setBackLastStatus(p => [...p, {slot: slot}])
            } else {
                setBackLastStatus(p => p.filter(r => r.slot !== slot))
            }
        }
        const newOutputs = outputs.map((o, i) => {
            if (i === slot) {
                return !o
            }
            return o == null ? null : false
        })
        rpc.setDirectOutput(rawDevice.gateway_id, rawDevice.unique_device_id, slot, backLastStatus.map(r => r?.slot).includes(slot) ? 0 : 1)
            .then(() => setOutputs(newOutputs))
            .catch(failure)
    }

    const inputClick = (slot: number) => {

        setInputLoading(l => l.map((v, i) => (i === slot ? true : v)));
        setTimeout(() => {
            setInputLoading(l => l.map((v, i) => (i === slot ? false : v)));
        }, 500);

        rpc.getDirectInput(rawDevice.gateway_id, rawDevice.unique_device_id, slot)
            .then((value) => setInputs(inputs => inputs.map((v, i) => {
                if (i === slot) {
                    return Boolean(value);
                }

                return v;
            })))
            .catch(failure);
    };

    const [dimmerTest, setDimmerTest] = useState(null);
    const dimOutputClick = () => {
        rpc.setDirectOutput(
            rawDevice.gateway_id,
            rawDevice.unique_device_id,
            (deviceType?.internal_type_id === 23 && 8) ||
                (deviceType?.internal_type_id === 4 && 4) ||
                (deviceType?.internal_type_id === 5 && 3), !dimmerTest ? 100 : 0
        ).then(() => setDimmerTest(p => !p))
    }

    useEffect(() => {
        setOutputs(createNullArray(deviceType?.output_count));
        setOutputLoading(createNullArray(deviceType?.output_count));
    }, [deviceType?.output_count]);

    useEffect(() => {
        setInputs(createNullArray(deviceType?.input_count));
        setInputLoading(createNullArray(deviceType?.input_count));
    }, [deviceType?.input_count]);

    const versionClick = useCallback(() => {
        setHardwareVersionResults([]);
        setOpenHardwareVersionPopup(true);
        setVersionCheckInProgress(true);
        
        rpc.versionInfo(rawDevice.gateway_id, rawDevice.unique_device_id)
            .catch(error => {
                setHardwareVersionResults([
                    "",
                    t('version_information'),
                    `${t('version')}: ${versionStatusControl}`,  
                    "..."
                ]);
                setVersionCheckInProgress(false);
            });
    }, [rpc, rawDevice.gateway_id, rawDevice.unique_device_id, t, versionStatusControl]);

    const rssiClick = useCallback(() => {
        rpc.rssiInfo(rawDevice.gateway_id, rawDevice.unique_device_id)
    }, [rpc, rawDevice.gateway_id, rawDevice.unique_device_id])
    
    const onCloseHardwareVersionPopup = useCallback(() => {
        setOpenHardwareVersionPopup(false);
        setHardwareVersionResults([]);
        setVersionCheckInProgress(false);
    }, []);

    return (
        <div className={`io-test-modal ${orientation}`}>
            <div className='top'>
                <div className='left'>
                    <span className='title'>{t('io_test')}</span>
                    <img className='image' src={deviceImage} alt=''/>
                    <span className='name'>{deviceType?.name || '-'}</span>
                </div>
                <div className='middle'>
                    <div className='label-text-container'>
                        <span className='label'>
                            <span className='label-title'>{t('device_name')}:</span> {deviceName}
                        </span>
                    </div>
                    <div className='label-text-container'>
                        <span className='label'>
                            <span className='label-title'>{t('unique_id')}:</span> {rawDevice.unique_device_id}
                        </span>
                    </div>
                    {/*<div className='label-text-container'>*/}
                    {/*    <span className='label'><span className='label-title'>{t('VERSION')}:</span> 1.27</span>*/}
                    {/*</div>*/}
                    <div className='label-text-container'>
                        <span className='label'>
                            <span className='label-title'>{t('rf_power')}:</span> {rawDevice.rf_power.toString()}
                        </span>
                    </div>
                    <div className="iotest-button-container">
                        <MatchedItemButton
                            color='version'
                            text={t('version')}
                            onClick={versionClick}/>
                        <MatchedItemButton
                            color='rssi'
                            text={'RSSI'}
                            onClick={rssiClick}/>
                    </div>
                </div>
                <div className='right'>
                    <span className='close-text' {...onClickHandler(onClosePressed)}>{t('close')}</span>
                    <div className="attention-text">
                        <img src={IconAttention} alt="attention" />
                        <span>{t('match_screen_curtain_warning')}</span>
                    </div>
                </div>
            </div>
            <div className='bottom'>
                <div className='inputs'>
                    {
                        inputs.map((state, i) => {
                            const disabled = inputLoading[i];
                            return (
                                <div key={`input_${i}`} onClick={disabled ? undefined : () => inputClick(i)}>
                                    <span>{`IN${i}`}</span>
                                    <div className={'input-button' + (inputLoading[i] ? '' : (state == null ? '' : (state ? ' green' : ' red')))}>
                                        {inputLoading[i] && <Loader />}
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
                <div className="outputs">
                    {renderIf(!deviceType.is_io_device)(
                        () =>
                            outputs.map((state, i) => {
                                const disabled = outputLoading[i];
                                return (
                                    <div key={`output_${i}`} onClick={disabled ? undefined : () => outputClick(i)}>
                                        <span>{`Q${i}`}</span>
                                        <div className={'output-button' + (outputLoading[i] ? '' : (state == null ? '' : (state ? ' green' : ' red')))}>
                                            {outputLoading[i] && <Loader />}
                                        </div>
                                    </div>
                                );
                            }),
                        () =>
                            outputs.map((state, i) => {
                                const disabled = outputLoading[i];
                                return (
                                    <div
                                        key={`output_${i}`}
                                        onClick={disabled ? undefined : () => outputClickForIODevices(i)}
                                        className="special-io"
                                    >
                                        <span>{`Q${i}`}</span>
                                        <div className={'output-button' + (outputLoading[i] ? '' : (backLastStatus.map(r => r?.slot).includes(i) ? ' green' : ''))}>
                                            {outputLoading[i] && <Loader />}
                                        </div>
                                    </div>
                                );
                            })
                    )}
                    {renderIf(
                        deviceType?.internal_type_id === 23 ||
                            deviceType?.internal_type_id === 4 ||
                            deviceType?.internal_type_id === 5
                    )(() => (
                        <div onClick={dimOutputClick}>
                            <span>DIM</span>
                            <div
                                className={"output-button" + (dimmerTest == null ? "" : dimmerTest ? " green" : " red")}
                            />
                        </div>
                    ))}
                </div>
            </div>
            
            {renderIf(openHardwareVersionPopup)(() => (
                <MappingLogPopupScreen
                    logs={hardwareVersionResults
                        ? hardwareVersionResults
                        : [t("operation_is_in_progress")] 
                    }
                    isDone={!versionCheckInProgress}
                    onClickClose={onCloseHardwareVersionPopup}
                    isFormatted={true}
                />
            ))}
        </div>
    )
});
